import {
  BigSearchBarMagnifyingGlassIcon,
  ExternalPageIcon,
  GlobalOrdersIcon,
  GlobalSearch,
  MagnifyingGlassIcon,
  NewSearchCasesSuitCaseIcon,
  NewSearchDevicesPhoneIcon,
  NewSearchOrdersHandIcon,
  NewSearchSitesIcon,
} from '@/assets/svgIcons'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { globalSearch } from '@/services/apis/api'
import { debounce, formatDefPayload } from '@/lib/utils'
import { useState, useRef, useEffect } from 'react'
import { AxiosResponse } from 'axios'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/hooks/storeHooks/storehooks'
import {
  setDev,
  setNavigatedSite,
} from '@/redux-store/slices/siteListSlice/siteListSlice'
import { payloadDefaultProp } from '../dynamicDropdown'

import SearchInput from '@/containers/header/Search/input/SearchInput'
import SearchResultSection from '@/containers/header/Search/resultSection/SearchResultSection'
import SearchResultItem from '@/containers/header/Search/resultItem/SearchResultItem'
import {
  SearchProps,
  siteData,
  deviceLst,
  Orders,
  caseList,
} from './searchTypes'
import GradiantSkelton from '@/components/atom/gradiantSkelton/gradiantSkelton'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { SearchDetailModal } from './detailModal/SearchDetailModal'

const formatSearchValues = (
  ...values: (string | number | undefined | null)[]
): string => {
  return values
    .map(val => (val !== undefined && val !== null ? String(val).trim() : ''))
    .filter(val => val !== '')
    .map(val => `[${val}]`)
    .join('-')
}

const formatSearchValuesPlain = (
  ...values: (string | number | undefined | null)[]
): string => {
  return values
    .map(val => (val !== undefined && val !== null ? String(val).trim() : ''))
    .filter(val => val !== '')
    .join(' - ')
}

interface SearchComponentProps {
  variant?: 'mobile' | 'desktop'
}

function Search({ variant = 'desktop' }: SearchComponentProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const defPayload = useAppSelector(
    state => state.menu.payloadDef,
  ) as payloadDefaultProp

  const essentialIds = formatDefPayload(defPayload)
  const mobileCheck = useMediaQuery('(max-width: 640px)')

  const [searchData, setSearchData] = useState<SearchProps>({
    siteList: [],
    caseList: [],
    deviceList: [],
    orders: [],
  })
  const [open, setOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [searchVal, setSearchVal] = useState('')
  const [inputValue, setInputValue] = useState('')
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState<{
    data: siteData | deviceLst | Orders | caseList
    type: 'site' | 'device' | 'order' | 'case'
  } | null>(null)

  const inputRef = useRef<HTMLInputElement>(null)
  const searchRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
  const abortControllerRef = useRef<AbortController | null>(null)
  const currentRequestIdRef = useRef<number>(0)

  // Auto-focus input when search opens on mobile
  useEffect(() => {
    if (open && mobileCheck && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
    }
  }, [open, mobileCheck])

  const highlightText = (
    text: string,
    searchTerm: string,
  ): React.ReactNode[] => {
    if (!searchTerm.trim()) return [text]
    const alphanumericSearchTerm = searchTerm.replace(/[^a-zA-Z0-9\s]/g, '')
    const searchTerms = alphanumericSearchTerm
      .split(/\s+/)
      .filter(term => term.length > 0)
    const regexPatterns = searchTerms.map(term => new RegExp(`(${term})`, 'gi'))
    let parts: (string | React.ReactNode)[] = [text]
    regexPatterns.forEach(regex => {
      parts = parts.flatMap(part => {
        if (typeof part === 'string') {
          return part.split(regex).map((subPart, index) =>
            regex.test(subPart) ? (
              <strong
                key={`${subPart}-${index}`}
                className='text-accent font-semibold'
              >
                {subPart}
              </strong>
            ) : (
              subPart
            ),
          )
        }
        return part
      })
    })

    return parts
  }

  const getSiteAddressContent = (
    value: siteData['value'],
    searchTerm: string,
  ): React.ReactNode[] => {
    const { address, serviceAddress } = value
    const hasAddress = address?.trim().length > 0
    const hasServiceAddress = serviceAddress?.trim().length > 0

    // Determine which address fields actually contain the search term
    const lowerSearch = searchTerm.toLowerCase()
    const matchesBilling =
      hasAddress && address.toLowerCase().includes(lowerSearch)
    const matchesService =
      hasServiceAddress && serviceAddress.toLowerCase().includes(lowerSearch)

    // Neither address matched — match was on name / ID.
    // Show service address as plain unlabeled context (no pill).
    if (!matchesBilling && !matchesService) {
      const displayAddr = hasServiceAddress
        ? serviceAddress
        : hasAddress
          ? address
          : null
      if (!displayAddr) return []
      return [
        <span key='addr-plain' className='leading-snug opacity-70'>
          {highlightText(displayAddr, searchTerm)}
        </span>,
      ]
    }

    // Pill styles (only rendered when the address was the match)
    const pillBase =
      'inline-flex items-center px-1.5 py-[1px] rounded-sm text-[0.6em] font-semibold uppercase tracking-wider shrink-0 mt-[3px] leading-none'
    const pillAccent = `${pillBase} bg-info-dim-light text-info-forground`

    // Both match and are identical → combined pill
    if (
      matchesBilling &&
      matchesService &&
      address.trim() === serviceAddress.trim()
    ) {
      return [
        <span key='addr-same' className='flex items-start gap-2 mt-0.5'>
          <span className={pillAccent}>Billing &amp; Service</span>
          <span className='leading-snug'>
            {highlightText(address, searchTerm)}
          </span>
        </span>,
      ]
    }

    // Show only the address(es) that matched the search term
    const lines: React.ReactNode[] = []
    if (matchesBilling) {
      lines.push(
        <span key='billing' className='flex items-start gap-2 mt-0.5'>
          <span className={pillAccent}>Billing</span>
          <span className='leading-snug'>
            {highlightText(address, searchTerm)}
          </span>
        </span>,
      )
    }
    if (matchesService) {
      lines.push(
        <span key='service' className='flex items-start gap-2 mt-0.5'>
          <span className={pillAccent}>Service</span>
          <span className='leading-snug'>
            {highlightText(serviceAddress, searchTerm)}
          </span>
        </span>,
      )
    }
    return lines
  }

  const CustomResultItem = ({
    content,
    onClick,
  }: {
    content: React.ReactNode[]
    onClick: () => void
  }) => {
    return (
      <div className='flex justify-between items-center w-full px-3 py-1.5 my-0.5 transition-all rounded-lg hover:bg-primary-hover'>
        <div className='flex flex-col flex-1 min-w-0 pr-3'>
          {content.map((line, i) => {
            if (i === 0) {
              return (
                <span
                  key={i}
                  className='text-sm font-semibold text-primary-foreground leading-normal'
                >
                  {line}
                </span>
              )
            }
            return (
              <span
                key={i}
                className='text-xs font-normal text-secondary-foreground mt-0.5 leading-normal'
              >
                {line}
              </span>
            )
          })}
        </div>
        <div
          onClick={onClick}
          className='flex items-center justify-center text-primary-foreground flex-shrink-0 transition-colors cursor-pointer hover:text-accent p-0.5'
        >
          <ExternalPageIcon />
        </div>
      </div>
    )
  }

  const latestEssentialIds = useRef(essentialIds)

  useEffect(() => {
    latestEssentialIds.current = essentialIds
  }, [essentialIds])

  const handleSearch = useRef(
    debounce(async (query: unknown) => {
      const requestId = ++currentRequestIdRef.current
      setSearchVal(query as string)
      setIsLoading(true)

      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      abortControllerRef.current = new AbortController()

      const payload = { searchQuery: query, ...latestEssentialIds.current }

      try {
        const res = await globalSearch(
          payload,
          abortControllerRef.current?.signal,
        )
        if (requestId === currentRequestIdRef.current) {
          const response = res.data as AxiosResponse<SearchProps>
          setSearchData(
            response?.data || {
              siteList: [],
              caseList: [],
              deviceList: [],
              orders: [],
            },
          )
        }
      } catch (error) {
        if (requestId === currentRequestIdRef.current) {
          console.error('Search Error:', error)
        }
      } finally {
        if (requestId === currentRequestIdRef.current) {
          setIsLoading(false)
        }
      }
    }, 500),
  ).current

  const handleChange = (val: string) => {
    setInputValue(val)
    handleSearch(val)
  }

  const clearInput = () => {
    setInputValue('')
    setSearchVal('')
    setSearchData({
      siteList: [],
      caseList: [],
      deviceList: [],
      orders: [],
    })
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    if (inputRef.current) {
      inputRef.current.focus()
      inputRef.current.value = ''
    }
  }

  const handleScrollToResult = (section: string) => {
    const el = searchRefs.current[section]
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        setOpen(false)
      }
    }

    if (mobileCheck && open) {
      document.addEventListener('keydown', handleKeyDown)
      // Prevent body scroll when search is open on mobile
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'unset'
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [open, mobileCheck])

  // Handlers
  const handleClick = {
    site: (data: siteData) => {
      if (mobileCheck) {
        setSelectedItem({ data, type: 'site' })
        setDetailModalOpen(true)
        return
      }

      const targetPath = '/technologyandnetworks/sitepage'
      const targetState = { siteId: data.value.siteId }

      if (location.state?.siteId === targetState.siteId) {
        setOpen(false)
        return
      }

      dispatch(
        setNavigatedSite({
          value: data.value.customerId,
          label: data.value.name,
          Rev_io_Account_ID__c: data.value.siteId,
          Id: data.id,
        }),
      )
      navigate(targetPath, { state: targetState })
      setOpen(false)
    },

    device: (data: deviceLst) => {
      if (mobileCheck) {
        setSelectedItem({ data, type: 'device' })
        setDetailModalOpen(true)
        return
      }

      const targetPath = '/technologyandnetworks/activeservices'
      const targetState = { siteId: data.value.siteId }

      // If already on the same device page with same siteId, just close search
      if (location.state?.siteId === targetState.siteId) {
        setOpen(false)
        return
      }

      dispatch(
        setNavigatedSite({
          value: data.value.customerId,
          label: data.value.name,
          Rev_io_Account_ID__c: data.value.siteId,
          Id: data.id,
        }),
      )
      dispatch(setDev(data.value))
      navigate(targetPath, { state: targetState })
      setOpen(false)
    },

    order: (data: Orders) => {
      if (mobileCheck) {
        setSelectedItem({ data, type: 'order' })
        setDetailModalOpen(true)
        return
      }

      const targetPath = `/service-delivery/orders/${data.value.orderId}`
      const targetState = { siteId: data.value.siteId }
      if (location.state?.siteId === targetState.siteId) {
        setOpen(false)
        return
      }
      dispatch(
        setNavigatedSite({
          value: data.value.customerId,
          label: data.value.companyName,
          Rev_io_Account_ID__c: data.value.siteId,
          Id: data.id,
        }),
      )
      navigate(targetPath, { state: targetState })
      setOpen(false)
    },
    case: (data: caseList) => {
      if (mobileCheck) {
        setSelectedItem({ data, type: 'case' })
        setDetailModalOpen(true)
        return
      }

      const targetPath = `/support/casemanagement/${data.value.caseNumber}`
      const targetState = { siteId: data.value.siteId }

      // If already on the same case page, just close search
      if (location.state?.siteId === targetState.siteId) {
        setOpen(false)
        return
      }

      dispatch(
        setNavigatedSite({
          value: data.value.customerId,
          label: data.value.accountName,
          Rev_io_Account_ID__c: data.value.siteId,
          Id: data.id,
        }),
      )
      navigate(targetPath, { state: targetState })
      setOpen(false)
    },
  }

  if (mobileCheck) {
    return (
      <>
        <button
          data-testid={`test-global_search_${variant}`}
          onClick={() => setOpen(true)}
          className='h-11 w-11 flex justify-center items-center rounded-full focus:outline-none hover:scale-110'
          aria-label='Search'
        >
          <MagnifyingGlassIcon />
        </button>

        <SearchDetailModal
          open={detailModalOpen}
          onOpenChange={setDetailModalOpen}
          item={selectedItem?.data || null}
          type={selectedItem?.type || null}
        />

        {open && (
          <div className='fixed inset-0 w-screen h-screen bg-primary z-[9999] flex flex-col'>
            <div className='p-4 pb-0'>
              <div className='flex items-center gap-3 mb-4'>
                <SearchInput
                  data-testid={`test-global_search_text_${variant}`}
                  inputValue={inputValue}
                  onChange={handleChange}
                  onClear={clearInput}
                  inputRef={inputRef}
                />
                <button
                  onClick={() => setOpen(false)}
                  className='text-primary-foreground hover:opacity-80 flex-shrink-0'
                  aria-label='Close search'
                >
                  <svg
                    className='w-6 h-6'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className='flex-1 text-primary-foreground overflow-y-auto spec-scroll px-4 pb-4'>
              {searchVal && searchVal.length > 1 && !isLoading && (
                <div className='flex flex-col gap-3 mb-4'>
                  {searchData?.siteList?.length > 0 && (
                    <div
                      className='flex items-center gap-3 cursor-pointer'
                      onClick={() => handleScrollToResult('siteList')}
                    >
                      <NewSearchSitesIcon />
                      <span
                        className={`${mobileCheck ? 'text-base' : 'text-xl'} text-primary-foreground font-normal`}
                      >
                        Sites
                      </span>
                      <span className='inline-flex items-center text-[0.8125rem] font-normal leading-normal justify-center w-auto px-padding-md h-[17px] bg-info-dim-light text-info-forground rounded-radius-full'>
                        {searchData.siteList.length}
                      </span>
                    </div>
                  )}
                  {searchData.caseList.length > 0 && (
                    <div
                      className='flex items-center gap-3 cursor-pointer'
                      onClick={() => handleScrollToResult('caseList')}
                    >
                      <NewSearchCasesSuitCaseIcon />
                      <span
                        className={`${mobileCheck ? 'text-base' : 'text-xl'} mobilecheck:text-primary-foreground font-normal`}
                      >
                        Case
                      </span>
                      <span className='inline-flex items-center text-[0.8125rem] font-normal leading-normal justify-center w-auto px-padding-md h-[17px] bg-info-dim-light text-info-forground rounded-radius-full'>
                        {searchData.caseList.length}
                      </span>
                    </div>
                  )}
                  {searchData.deviceList.length > 0 && (
                    <div
                      className='flex items-center gap-3 cursor-pointer'
                      onClick={() => handleScrollToResult('deviceList')}
                    >
                      <NewSearchDevicesPhoneIcon />
                      <span
                        className={`${mobileCheck ? 'text-base' : 'text-xl'} mobilecheck:text-primary-foreground font-normal`}
                      >
                        Devices
                      </span>
                      <span className='inline-flex items-center text-[0.8125rem] font-normal leading-normal justify-center w-auto px-padding-md h-[17px] bg-info-dim-light text-info-forground rounded-radius-full'>
                        {searchData.deviceList.length}
                      </span>
                    </div>
                  )}
                  {searchData.orders.length > 0 && (
                    <div
                      className='flex items-center gap-3 cursor-pointer'
                      onClick={() => handleScrollToResult('orders')}
                    >
                      <GlobalOrdersIcon />
                      <span
                        className={`${mobileCheck ? 'text-base' : 'text-xl'} mobilecheck:text-primary-foreground font-normal`}
                      >
                        Orders
                      </span>
                      <span className='inline-flex items-center text-[0.8125rem] font-normal leading-normal justify-center w-auto px-padding-md h-[17px] bg-info-dim-light text-info-forground rounded-radius-full'>
                        {searchData.orders.length}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {!searchVal ? (
                <div className='grid place-items-center h-full'>
                  <div className='flex flex-col justify-center items-center'>
                    <BigSearchBarMagnifyingGlassIcon />
                    <div className='text-sm'>Start typing to search...</div>
                  </div>
                </div>
              ) : searchVal.length === 1 ? (
                <div className='text-center text-sm h-full flex flex-col justify-center items-center'>
                  Please enter more than 1 character to search.
                </div>
              ) : isLoading ? (
                <div className='text-center'>
                  <div className='flex flex-col gap-6 mt-3'>
                    <div className=' ml-3 flex flex-col gap-3'>
                      {[1, 2, 3, 4].map((_, i) => (
                        <div key={i} className='flex items-center gap-2'>
                          <GradiantSkelton
                            width='w-20'
                            height='h-6'
                            className='rounded-full'
                          />
                        </div>
                      ))}
                    </div>
                    <hr className='border border-secondary-border' />
                    <div className='flex items-center gap-2'>
                      <GradiantSkelton
                        width='w-4'
                        height='h-4'
                        className='rounded-full'
                      />
                      <GradiantSkelton
                        width='w-20'
                        height='h-5'
                        className='rounded-full'
                      />
                    </div>
                    {[1, 2].map((_, idx) => (
                      <div key={idx} className='flex flex-col gap-2 ml-6'>
                        <GradiantSkelton
                          width='w-32'
                          height='h-4'
                          className='rounded'
                        />
                        <GradiantSkelton
                          width='w-4/5'
                          height='h-4'
                          className='rounded'
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : !isLoading &&
                !searchData.siteList?.length &&
                !searchData.caseList?.length &&
                !searchData.deviceList?.length &&
                !searchData.orders?.length ? (
                <div className='text-center'>No results found</div>
              ) : (
                <div className='my-2'>
                  <hr className='border border-secondary-border mb-4' />
                  {searchData.siteList.length > 0 && (
                    <SearchResultSection
                      title='Sites'
                      icon={<NewSearchSitesIcon />}
                      count={searchData.siteList.length}
                      sectionRef={el => (searchRefs.current['siteList'] = el)}
                      onClickScroll={() => handleScrollToResult('siteList')}
                    >
                      {searchData.siteList.map(site => (
                        <SearchResultItem
                          key={site.id}
                          onClick={() => handleClick.site(site)}
                          content={[
                            highlightText(
                              formatSearchValuesPlain(
                                site.value.customerId,
                                site.value.siteId,
                              ),
                              searchVal,
                            ),
                            highlightText(
                              formatSearchValues(
                                site.value.address,
                                site.value.name,
                              ),
                              searchVal,
                            ),
                          ]}
                        />
                      ))}
                    </SearchResultSection>
                  )}

                  {searchData.caseList.length > 0 && (
                    <SearchResultSection
                      title='Cases'
                      icon={<NewSearchCasesSuitCaseIcon />}
                      count={searchData.caseList.length}
                      sectionRef={el => (searchRefs.current['caseList'] = el)}
                      onClickScroll={() => handleScrollToResult('caseList')}
                    >
                      {searchData.caseList.map(caseItem => (
                        <SearchResultItem
                          key={caseItem.id}
                          onClick={() => handleClick.case(caseItem)}
                          content={[
                            highlightText(
                              formatSearchValuesPlain(
                                caseItem.value.accountName,
                                caseItem.value.caseNumber,
                              ),
                              searchVal,
                            ),
                            highlightText(
                              formatSearchValues(
                                caseItem.value.siteId,
                                caseItem.value.customerId,
                              ),
                              searchVal,
                            ),
                            highlightText(
                              formatSearchValues(
                                caseItem.value.subject,
                                caseItem.value.caseType,
                              ),
                              searchVal,
                            ),
                          ]}
                        />
                      ))}
                    </SearchResultSection>
                  )}

                  {searchData.orders.length > 0 && (
                    <SearchResultSection
                      title='Orders'
                      icon={<GlobalOrdersIcon />}
                      count={searchData.orders.length}
                      sectionRef={el => (searchRefs.current['orders'] = el)}
                      onClickScroll={() => handleScrollToResult('orders')}
                    >
                      {searchData.orders.map(order => (
                        <SearchResultItem
                          key={order.id}
                          onClick={() => handleClick.order(order)}
                          content={[
                            highlightText(
                              formatSearchValuesPlain(
                                order.value.customerId,
                                order.value.siteId,
                                order.value.companyName,
                              ),
                              searchVal,
                            ),
                            highlightText(
                              formatSearchValues(
                                order.value.pon,
                                order.value.orderId,
                                order.value.circuitNumber,
                              ),
                              searchVal,
                            ),
                            highlightText(
                              formatSearchValues(
                                order.value.status,
                                order.value.orderTrackingNumber,
                              ),
                              searchVal,
                            ),
                          ]}
                        />
                      ))}
                    </SearchResultSection>
                  )}

                  {searchData.deviceList.length > 0 && (
                    <SearchResultSection
                      title='Devices'
                      icon={<NewSearchDevicesPhoneIcon />}
                      count={searchData.deviceList.length}
                      sectionRef={el => (searchRefs.current['deviceList'] = el)}
                      onClickScroll={() => handleScrollToResult('deviceList')}
                    >
                      {searchData.deviceList.map(device => (
                        <SearchResultItem
                          key={device.id}
                          onClick={() => handleClick.device(device)}
                          content={[
                            highlightText(
                              formatSearchValuesPlain(
                                device.value.lineId,
                                device.value.siteId,
                              ),
                              searchVal,
                            ),
                            highlightText(
                              formatSearchValues(
                                device.value.customerId,
                                device.value.name,
                              ),
                              searchVal,
                            ),
                            highlightText(
                              formatSearchValues(
                                device.value.circuitId,
                                device.value.address,
                              ),
                              searchVal,
                            ),
                            highlightText(
                              formatSearchValues(
                                device.value.serviceType,
                                device.value.nodeId,
                              ),
                              searchVal,
                            ),
                            highlightText(device.value.lanIp, searchVal),
                          ]}
                        />
                      ))}
                    </SearchResultSection>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        id={variant === 'desktop' ? 'tour-global-search' : undefined}
        data-testid={`test-global_search_${variant}`}
        className='p-[0.5rem] bg-tertiary flex justify-center items-center rounded-radius-md focus:outline-none hover:scale-110'
      >
        {/* <MagnifyingGlassIcon h={28} w={28} /> */}
        <GlobalSearch />
      </PopoverTrigger>

      <PopoverContent
        side='bottom'
        sideOffset={10}
        className='bg-primary text-primary-foreground border border-border rounded-xl shadow-lg p-4 w-[500px] max-h-[60vh] flex flex-col z-[9999] mt-3 mr-4'
      >
        {/* Modern Search Input */}
        <div className='relative w-full mb-3 flex items-center'>
          <span className='absolute left-0'>
            <MagnifyingGlassIcon />
          </span>
          <input
            ref={inputRef}
            data-testid={`test-global_search_text_${variant}`}
            type='text'
            className='w-full pl-9 pr-8 py-1.5 text-sm bg-primary text-primary-foreground border border-border rounded-lg shadow-sm'
            placeholder='Search'
            value={inputValue}
            onChange={e => handleChange(e.target.value)}
            maxLength={50}
          />
          {inputValue && (
            <button
              onClick={clearInput}
              className='absolute right-3 text-primary-foreground  transition-colors'
              aria-label='Clear search'
            >
              <svg
                className='w-3.5 h-3.5'
                fill='none'
                stroke='currentColor'
                strokeWidth={2}
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </button>
          )}
        </div>

        {/* Categories Banner & Pill Filters */}
        {(() => {
          const totalResults =
            (searchData?.siteList?.length || 0) +
            (searchData?.caseList?.length || 0) +
            (searchData?.deviceList?.length || 0) +
            (searchData?.orders?.length || 0)
          if (
            searchVal &&
            searchVal.length > 1 &&
            !isLoading &&
            totalResults > 0
          ) {
            return (
              <div className='border border-border bg-primary rounded-xl p-2.5 mb-3 flex flex-col gap-1.5'>
                <span className='text-sm font-semibold text-secondary-foreground'>
                  {totalResults} results found
                </span>
                <div className='flex flex-wrap gap-1.5 mt-0.5'>
                  {searchData.siteList.length > 0 && (
                    <button
                      onClick={() => handleScrollToResult('siteList')}
                      className='px-2.5 py-0.5 text-xs bg-tertiary rounded-full flex items-center gap-1 transition-all text-primary-foreground font-normal'
                    >
                      Sites{' '}
                      <span className='font-semibold text-primary-foreground'>
                        {searchData.siteList.length}
                      </span>
                    </button>
                  )}
                  {searchData.caseList.length > 0 && (
                    <button
                      onClick={() => handleScrollToResult('caseList')}
                      className='px-2.5 py-0.5 text-xs bg-tertiary rounded-full flex items-center gap-1 transition-all text-primary-foreground font-normal'
                    >
                      Cases{' '}
                      <span className='font-semibold text-primary-foreground'>
                        {searchData.caseList.length}
                      </span>
                    </button>
                  )}
                  {searchData.deviceList.length > 0 && (
                    <button
                      onClick={() => handleScrollToResult('deviceList')}
                      className='px-2.5 py-0.5 text-xs bg-tertiary rounded-full flex items-center gap-1 transition-all text-primary-foreground font-normal'
                    >
                      Devices{' '}
                      <span className='font-semibold text-primary-foreground'>
                        {searchData.deviceList.length}
                      </span>
                    </button>
                  )}
                  {searchData.orders.length > 0 && (
                    <button
                      onClick={() => handleScrollToResult('orders')}
                      className='px-2.5 py-0.5 text-xs bg-tertiary rounded-full flex items-center gap-1 transition-all text-primary-foreground font-normal'
                    >
                      Orders{' '}
                      <span className='font-semibold text-primary-foreground'>
                        {searchData.orders.length}
                      </span>
                    </button>
                  )}
                </div>
              </div>
            )
          }
          return null
        })()}

        {/* Scrollable Results List */}
        <div className='flex-1 overflow-y-auto spec-scroll pr-1 text-primary-foreground'>
          {!searchVal ? (
            <div className='grid place-items-center h-full text-primary-foreground'>
              <div className='flex flex-col justify-center items-center py-8'>
                <BigSearchBarMagnifyingGlassIcon />
                <div className='mt-2 font-medium text-sm'>
                  Start typing to search...
                </div>
              </div>
            </div>
          ) : searchVal.length === 1 ? (
            <div className='text-center py-8 text-primary-foreground text-sm'>
              Please enter more than 1 character to search.
            </div>
          ) : isLoading ? (
            <div className='text-center py-4'>
              <div className='flex flex-col gap-6'>
                <div className='flex flex-col gap-3'>
                  {[1, 2, 3].map((_, i) => (
                    <div key={i} className='flex items-center gap-2'>
                      <GradiantSkelton
                        width='w-24'
                        height='h-6'
                        className='rounded-full'
                      />
                    </div>
                  ))}
                </div>
                <hr className='border-slate-100' />
                <div className='flex items-center gap-2'>
                  <GradiantSkelton
                    width='w-4'
                    height='h-4'
                    className='rounded-full'
                  />
                  <GradiantSkelton
                    width='w-20'
                    height='h-5'
                    className='rounded-full'
                  />
                </div>
                {[1, 2].map((_, idx) => (
                  <div key={idx} className='flex flex-col gap-2 ml-6'>
                    <GradiantSkelton
                      width='w-32'
                      height='h-4'
                      className='rounded'
                    />
                    <GradiantSkelton
                      width='w-4/5'
                      height='h-4'
                      className='rounded'
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : !isLoading &&
            !searchData?.siteList?.length &&
            !searchData?.caseList?.length &&
            !searchData?.deviceList?.length &&
            !searchData?.orders?.length ? (
            <div className='text-center py-12 text-primary-foreground h-full flex justify-center items-center text-sm'>
              No results found
            </div>
          ) : (
            <div className='flex flex-col'>
              {/* Sites Section */}
              {(searchData?.siteList?.length || 0) > 0 && (
                <div
                  ref={el => (searchRefs.current['siteList'] = el)}
                  className='mb-3'
                >
                  <div className='flex items-center text-primary-foreground font-semibold py-1.5 px-0.5 mb-1.5'>
                    <span className='flex-shrink-0'>
                      <NewSearchSitesIcon />
                    </span>
                    <span className='ml-1.5 text-sm font-medium text-primary-foreground'>
                      Sites ({searchData.siteList.length})
                    </span>
                  </div>
                  <div className='flex flex-col'>
                    {searchData.siteList.map(site => (
                      <CustomResultItem
                        key={site.id}
                        onClick={() => handleClick.site(site)}
                        content={[
                          highlightText(
                            formatSearchValuesPlain(
                              site.value.customerId,
                              site.value.siteId,
                            ),
                            searchVal,
                          ),
                          highlightText(site.value.name, searchVal),
                          ...getSiteAddressContent(site.value, searchVal),
                        ]}
                      />
                    ))}
                  </div>
                  <div className='h-[1px] bg-primary my-2' />
                </div>
              )}

              {/* Cases Section */}
              {(searchData?.caseList?.length || 0) > 0 && (
                <div
                  ref={el => (searchRefs.current['caseList'] = el)}
                  className='mb-3'
                >
                  <div className='flex items-center text-primary-foreground font-semibold py-1.5 px-0.5 mb-1.5'>
                    <span className=' flex-shrink-0'>
                      <NewSearchCasesSuitCaseIcon />
                    </span>
                    <span className='ml-1.5 text-sm font-medium text-primary-foreground'>
                      Cases ({searchData.caseList.length})
                    </span>
                  </div>
                  <div className='flex flex-col'>
                    {searchData.caseList.map(caseItem => (
                      <CustomResultItem
                        key={caseItem.id}
                        onClick={() => handleClick.case(caseItem)}
                        content={[
                          highlightText(
                            formatSearchValuesPlain(
                              caseItem.value.accountName,
                              caseItem.value.caseNumber,
                            ),
                            searchVal,
                          ),
                          highlightText(
                            formatSearchValues(
                              caseItem.value.siteId,
                              caseItem.value.customerId,
                            ),
                            searchVal,
                          ),
                          highlightText(
                            formatSearchValues(
                              caseItem.value.subject,
                              caseItem.value.caseType,
                            ),
                            searchVal,
                          ),
                        ]}
                      />
                    ))}
                  </div>
                  <div className='h-[1px] bg-primary my-2' />
                </div>
              )}

              {/* Devices Section */}
              {(searchData?.deviceList?.length || 0) > 0 && (
                <div
                  ref={el => (searchRefs.current['deviceList'] = el)}
                  className='mb-3'
                >
                  <div className='flex items-center text-primary-foreground font-semibold py-1.5 px-0.5 mb-1.5'>
                    <span className=' flex-shrink-0'>
                      <NewSearchDevicesPhoneIcon />
                    </span>
                    <span className='ml-1.5 text-sm font-medium text-primary-foreground'>
                      Devices ({searchData.deviceList.length})
                    </span>
                  </div>
                  <div className='flex flex-col'>
                    {searchData.deviceList.map(device => (
                      <CustomResultItem
                        key={device.id}
                        onClick={() => handleClick.device(device)}
                        content={[
                          highlightText(
                            formatSearchValuesPlain(
                              device.value.lineId,
                              device.value.siteId,
                            ),
                            searchVal,
                          ),
                          highlightText(
                            formatSearchValues(
                              device.value.customerId,
                              device.value.name,
                            ),
                            searchVal,
                          ),
                          highlightText(
                            formatSearchValues(
                              device.value.circuitId,
                              device.value.address,
                            ),
                            searchVal,
                          ),
                          highlightText(
                            formatSearchValues(
                              device.value.serviceType,
                              device.value.nodeId,
                            ),
                            searchVal,
                          ),
                          highlightText(device.value.lanIp, searchVal),
                        ]}
                      />
                    ))}
                  </div>
                  <div className='h-[1px] bg-slate-100 my-2' />
                </div>
              )}

              {/* Orders Section */}
              {(searchData?.orders?.length || 0) > 0 && (
                <div
                  ref={el => (searchRefs.current['orders'] = el)}
                  className='mb-3'
                >
                  <div className='flex items-center text-primary-foreground font-semibold py-1.5 px-0.5 mb-1.5'>
                    <span className=' flex-shrink-0'>
                      <NewSearchOrdersHandIcon />
                    </span>
                    <span className='ml-1.5 text-sm font-medium text-primary-foreground'>
                      Orders ({searchData.orders.length})
                    </span>
                  </div>
                  <div className='flex flex-col'>
                    {searchData.orders.map(order => (
                      <CustomResultItem
                        key={order.id}
                        onClick={() => handleClick.order(order)}
                        content={[
                          highlightText(
                            formatSearchValuesPlain(
                              order.value.customerId,
                              order.value.siteId,
                              order.value.companyName,
                            ),
                            searchVal,
                          ),
                          highlightText(
                            formatSearchValues(
                              order.value.pon,
                              order.value.orderId,
                              order.value.circuitNumber,
                            ),
                            searchVal,
                          ),
                          highlightText(
                            formatSearchValues(
                              order.value.status,
                              order.value.orderTrackingNumber,
                            ),
                            searchVal,
                          ),
                        ]}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default Search
